import * as React from 'react';
import calculateScrollOffset from '../../utils/calculateScrollOffset';
import type CalendarSurfaceStore from '../../store/schema/CalendarSurfaceStore';
import { areDateRangesEqual, DateRange } from 'owa-datetime-utils';
import { safeRequestAnimationFrame } from 'owa-performance';

export interface ScrollToWorkingHoursHooksProps {
    store: CalendarSurfaceStore;
    shouldScrollToWorkingHours?: boolean;
}

export default function useScrollToWorkingHoursHooks(props: ScrollToWorkingHoursHooksProps) {
    let parentElementRef = null as React.MutableRefObject<HTMLElement>;
    let [cachedDateRange, setCachedDateRange] = React.useState<DateRange | null>();
    let asyncScrollId = React.useRef<number>(0);

    const scrollToWorkingHours = React.useCallback(() => {
        let { store } = props;
        if (!areDateRangesEqual(cachedDateRange, store.dateRange)) {
            setCachedDateRange(store.dateRange);

            // wrap in requestAnimationFrame to prevent layout recalulation during rendering
            // prefer requestAnimationFrame to setTimeout because this is needed for initial render
            if (!asyncScrollId.current) {
                asyncScrollId.current = safeRequestAnimationFrame(() => {
                    if (parentElementRef?.current) {
                        parentElementRef.current.scrollTop = calculateScrollOffset(
                            store,
                            parentElementRef.current
                        );
                    }
                    asyncScrollId.current = 0;
                });
            }
        }
    }, [
        cachedDateRange,
        props.store,
        props.store.dateRange,
        // these are referenced in calculateScrollOffset, keep in sync
        props.store.userOptions,
        props.store.userOptions.workDaysWiseHoursStartTimeInMinutes,
        props.store.userOptions.workHoursStartTimeInMinutes,
    ]);

    React.useEffect(() => {
        if (props.shouldScrollToWorkingHours) {
            scrollToWorkingHours();
        }
    }, [
        props.shouldScrollToWorkingHours,
        scrollToWorkingHours,
        // should scroll to working hours when date changes
        props.store.selectedDate,
    ]);

    React.useEffect(() => {
        return () => window.cancelAnimationFrame(asyncScrollId.current);
    }, []);

    return (ref: React.MutableRefObject<HTMLElement>) => {
        parentElementRef = ref;

        if (props.shouldScrollToWorkingHours) {
            scrollToWorkingHours();
        }

        return {
            children: null,
            handlers: {},
        };
    };
}
